/**
 * OrbitPrint — "Edit sharing" for one printer (owner ask 2026-09-16).
 * Lists the organization's members; tick the ones who may print here DIRECTLY.
 * Unticked people can still send, but their document waits for a manager's OK.
 * Owners and admins always print directly and are shown locked.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { orbitApi, errText, useOrbitPath } from '../OrbitAuthContext';
import { OrbitButton } from './OrbitShell';

export default function SharingDialog({ printer, onClose, onSaved }) {
  const p = useOrbitPath();
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!printer) return;
    setRows(null);
    orbitApi.get(`/printers/${printer.id}/sharing`)
      .then((r) => { setRows(r.data.members); setDraft(Object.fromEntries(r.data.members.map((m) => [m.user_id, m.state]))); })
      .catch((err) => { toast.error(errText(err)); onClose(); });
  }, [printer]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!printer) return null;
  const save = async () => {
    setBusy(true);
    try {
      const entries = (rows || []).filter((m) => !m.locked).map((m) => ({ user_id: m.user_id, state: draft[m.user_id] || 'approval' }));
      await orbitApi.put(`/printers/${printer.id}/sharing`, { entries });
      toast.success('Sharing saved');
      onSaved?.(); onClose();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  const members = (rows || []).filter((m) => !m.locked);
  const managers = (rows || []).filter((m) => m.locked);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6" role="dialog" aria-modal="true" aria-label="Edit sharing"
      style={{ background: 'rgba(6,12,24,.72)', backdropFilter: 'blur(6px)' }} onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="orbit-root orbit-card is-kit w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-6 sm:p-8" style={{ minHeight: 'auto', background: 'var(--ob-surface)' }} data-testid="orbit-sharing-dialog">
        <p className="orbit-label m-0">Edit sharing</p>
        <h2 className="orbit-display mt-1" style={{ fontSize: 30 }}>{printer.name}</h2>
        <p className="text-sm orbit-ink2 mt-2">Tick the people who can print here directly. Everyone else can still send a document, but it waits for a manager's OK.</p>

        {rows === null ? (
          <p className="orbit-mono text-sm orbit-muted mt-5">Loading…</p>
        ) : (
          <>
            <ul className="list-none p-0 m-0 mt-5 orbit-panel divide-y" style={{ borderColor: 'var(--ob-rule)' }} data-testid="orbit-sharing-list">
              {managers.map((m) => (
                <li key={m.user_id} className="flex items-center gap-3 px-4 py-3 opacity-70">
                  <input type="checkbox" className="orbit-check" checked disabled />
                  <span className="flex-1 min-w-0"><span className="block font-semibold text-[15px] truncate">{m.full_name || m.email}</span><span className="block orbit-mono text-[11px] orbit-muted truncate">{m.email}</span></span>
                  <span className="orbit-label">{m.role} · always</span>
                </li>
              ))}
              {members.map((m) => (
                <li key={m.user_id} className="flex items-center gap-3 px-4 py-3">
                  <input type="checkbox" className="orbit-check" checked={draft[m.user_id] === 'allowed'}
                    onChange={(e) => setDraft((d) => ({ ...d, [m.user_id]: e.target.checked ? 'allowed' : 'approval' }))}
                    data-testid={`orbit-sharing-${m.user_id}`} />
                  <span className="flex-1 min-w-0"><span className="block font-semibold text-[15px] truncate">{m.full_name || m.email}</span><span className="block orbit-mono text-[11px] orbit-muted truncate">{m.email}</span></span>
                  <span className="orbit-label" style={{ color: draft[m.user_id] === 'allowed' ? 'var(--ob-green)' : 'var(--ob-amber)' }}>
                    {draft[m.user_id] === 'allowed' ? 'prints directly' : 'needs approval'}
                  </span>
                </li>
              ))}
              {members.length === 0 && (
                <li className="px-4 py-4 text-sm orbit-ink2">Only managers so far. <Link to={p('/members')} className="orbit-link">Invite people</Link> and they show up here.</li>
              )}
            </ul>
            <div className="mt-5 flex justify-end gap-3">
              <OrbitButton variant="quiet" size="small" onClick={onClose} disabled={busy}>Cancel</OrbitButton>
              <OrbitButton size="small" onClick={save} busy={busy} testid="orbit-sharing-save">Save sharing</OrbitButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
